import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import API from "../api";
import { PlusCircle, Trash2, Save, Share2, Globe, Lock } from "lucide-react";
import { useToast } from "../context/ToastContext";

function CreatePublicTest() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [testType, setTestType] = useState("practice");
  const [questions, setQuestions] = useState([]);
  const [createdTestId, setCreatedTestId] = useState(null);
  const { showToast } = useToast();
  // ✅ Token check
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/login" replace />;

  // ✅ Add question
  const handleAddQuestion = () => {
  setQuestions([
    ...questions,
    {
      question: "",
      type: "mcq",
      media: null,   // 🔥 question image/video
      options: [
        { text: "", media: null },
        { text: "", media: null }
      ],
      answer: "",
      difficulty: "medium",
    },
  ]);
};


  // ✅ Remove question
  const handleRemoveQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  // ✅ Update question
  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  // ✅ Option logic
  const handleOptionChange = (qIndex, optIndex, value) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options[optIndex].text = value;
    setQuestions(newQuestions);
  };

  const MAX_OPTIONS = 8;

const handleAddOption = (qIndex) => {
  if (questions[qIndex].options.length >= MAX_OPTIONS) {
    return showToast("⚠️ Maximum 8 options allowed (A–H)");
  }

  const newQuestions = [...questions];
  newQuestions[qIndex].options.push({ text: "", media: null });
  setQuestions(newQuestions);
};


  const handleRemoveOption = (qIndex, optIndex) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options.splice(optIndex, 1);
    setQuestions(newQuestions);
  };

  // ✅ Submit test
// ✅ Submit test
const handleSubmit = async () => {
  if (!title.trim() || questions.length === 0) {
    return showToast("⚠️ Please add a title and at least one question!");
  }

  const userId = localStorage.getItem("userId"); // ✅ added
  if (!userId) return showToast("⚠️ Please log in again!");
// 🔥 ADD VALIDATION HERE
  if (questions.some(q => !q.question.trim())) {
    return showToast("⚠️ All questions must have text!");
  }

  if (questions.some(q => q.type === "mcq" && q.options.length < 2)) {
    return showToast("⚠️ MCQ must have at least 2 options!");
  }


if (questions.some(q =>
  q.type === "mcq" &&
  q.options.some(opt => !opt.text.trim())
)) {
  return showToast("⚠️ All options must have text!");
}


if (questions.some(q => q.type === "truefalse" && !q.answer)) {
  return showToast("⚠️ Please select True or False!");
}
  if (questions.some(q => q.answer === "" || q.answer === null)) {
    return showToast("⚠️ Please select correct answer for all questions!");
  }
  try {
    const res = await API.post(
      "/tests/create",
      {
        title,
        description,
        type: testType,
        visibility: isPublic ? "public" : "private",
        questions: questions.map(q => {

  if (q.type === "truefalse") {
  return {
    question: q.question,
    media: q.media,
    type: "truefalse",
    options: [
      { key: "A", text: "True" },
      { key: "B", text: "False" }
    ],
    answer: q.answer,   // ✅ direct true / false
    difficulty: q.difficulty
  };
}

  if (q.type === "mcq") {

  if (q.answer === "" || q.answer === null) {
    throw new Error("MCQ answer missing");
  }

  return {
    question: q.question,
    media: q.media,
    type: "mcq",
    options: q.options.map((opt, i) => ({
      key: String.fromCharCode(65 + i),
      text: opt.text,
      media: opt.media
    })),
    answer: String.fromCharCode(65 + Number(q.answer)),
    difficulty: q.difficulty
  };
}

  return {
    question: q.question,
    media: q.media,
    type: "text",
    options: [],
    answer: q.answer,
    difficulty: q.difficulty
  };

        }),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    showToast(`✅ ${testType.toUpperCase()} Test created successfully!`);
    setCreatedTestId(res.data.test?._id || null);
    setTitle("");
    setDescription("");
    setQuestions([]);
  } catch (err) {
    console.error(err);
    showToast("❌ Error creating test");
  }
};


  // ✅ Share
  const handleShare = () => {
    if (!createdTestId) return showToast("⚠️ Create a test first!");
    const shareLink = `${window.location.origin}/test/${createdTestId}`;
    navigator.clipboard.writeText(shareLink);
    showToast(`✅ Test link copied:\n${shareLink}`);
  };

  return (
    <div className="max-w-5xl mx-auto mt-12 bg-white shadow-2xl rounded-2xl p-8 border border-gray-200">
      <h2 className="text-4xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-700 mb-8">
        🧠 Create a New Public Test
      </h2>

      {/* Title */}
      <div className="mb-5">
        <label className="block mb-2 font-semibold text-gray-700">
          Test Title
        </label>
        <input
          type="text"
          placeholder="Enter an engaging title..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Description */}
      <div className="mb-5">
        <label className="block mb-2 font-semibold text-gray-700">
          Description
        </label>
        <textarea
          placeholder="Describe your test briefly..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Test Type */}
      <div className="mb-5">
        <label className="block mb-2 font-semibold text-gray-700">
          Select Test Type
        </label>
        <select
          value={testType}
          onChange={(e) => setTestType(e.target.value)}
          className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="practice">🧠 Practice Test</option>
          <option value="timed">⏱ Timed Test</option>
          <option value="exam">🚫 Exam Mode Test</option>
          <option value="adaptive">🧩 Adaptive Test</option>
        </select>
      </div>

      {/* Visibility */}
      <div className="mb-6 flex items-center justify-between bg-gray-100 px-5 py-4 rounded-xl border border-gray-200">
        <span className="font-semibold text-gray-700 flex items-center gap-2">
          {isPublic ? (
            <Globe className="text-blue-600" />
          ) : (
            <Lock className="text-gray-600" />
          )}
          Visibility
        </span>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="visibility"
              value="private"
              checked={!isPublic}
              onChange={() => setIsPublic(false)}
              className="text-indigo-600 focus:ring-indigo-500"
            />
            Private
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="visibility"
              value="public"
              checked={isPublic}
              onChange={() => setIsPublic(true)}
              className="text-indigo-600 focus:ring-indigo-500"
            />
            Public
          </label>
        </div>
      </div>

      {/* Questions Section */}
      {questions.map((q, qIndex) => (
        <div
          key={qIndex}
          className="mb-8 border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-sm hover:shadow-md transition"
        >
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-bold text-lg text-indigo-700">
              Question {qIndex + 1}
            </h4>
            <button
              onClick={() => handleRemoveQuestion(qIndex)}
              className="text-red-500 hover:text-red-700 transition flex items-center gap-1"
            >
              <Trash2 size={18} /> Remove
            </button>
          </div>

          {/* Question Text */}
          <input
  type="text"
  placeholder="Enter your question..."
  value={q.question}
  onChange={(e) =>
    handleQuestionChange(qIndex, "question", e.target.value)
  }
  className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-3 focus:ring-2 focus:ring-indigo-500"
/>

          <input
  type="file"
  accept="image/*,video/*"
  onChange={async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const res = await API.post("/upload", formData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const newQuestions = [...questions];
    newQuestions[qIndex].media = {
      url: res.data.url,
      type: file.type.startsWith("video") ? "video" : "image"
    };

    setQuestions(newQuestions);
  }}
/>


          {/* Type Dropdown */}
          <div className="mb-3 flex gap-4 items-center">
            <label className="font-medium">Type:</label>
            <select
              value={q.type}
              onChange={(e) =>
                handleQuestionChange(qIndex, "type", e.target.value)
              }
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="mcq">Multiple Choice</option>
              <option value="text">Text</option>
              <option value="truefalse">True / False</option>
            </select>

            {/* Difficulty (for adaptive) */}
            <select
              value={q.difficulty}
              onChange={(e) =>
                handleQuestionChange(qIndex, "difficulty", e.target.value)
              }
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          {/* MCQ Options */}
          {q.type === "mcq" && (
            <div>
              {q.options.map((opt, optIndex) => (
                <div key={optIndex} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    placeholder={`Option ${optIndex + 1}`}
                    value={opt.text}                    onChange={(e) =>
                      handleOptionChange(qIndex, optIndex, e.target.value)
                    }
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
  type="file"
  accept="image/*"
  onChange={async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const res = await API.post("/upload", formData, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const newQuestions = [...questions];
    newQuestions[qIndex].options[optIndex].media = {
      url: res.data.url,
      type: "image",
    };

    setQuestions(newQuestions);
  }}
/>

{opt.media && (
  <img
    src={opt.media.url}
    alt="Option"
    className="h-20 mt-2 rounded"
  />
)}

{q.media && (
  q.media.type === "video" ? (
    <video
      src={q.media.url}
      controls
      className="h-32 mt-2 rounded-lg"
    />
  ) : (
    <img
      src={q.media.url}
      alt="Question"
      className="h-32 mt-2 rounded-lg"
    />
  )
)}



                  <button
                    onClick={() => handleRemoveOption(qIndex, optIndex)}
                    className="text-red-500 hover:text-red-700"
                  >
                    ✖
                  </button>
                </div>
              ))}
              <button
                onClick={() => handleAddOption(qIndex)}
                className="text-indigo-600 hover:text-indigo-800 mt-2 flex items-center gap-1"
              >
                <PlusCircle size={18} /> Add Option
              </button>
            </div>
          )}

       {/* Answer */}
<div className="mt-4">

  {/* MCQ */}
  {q.type === "mcq" && (
    <select
      value={q.answer}
      onChange={(e) =>
        handleQuestionChange(qIndex, "answer", Number(e.target.value))
      }
      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
    >
      <option value="">Select Correct Option</option>
      {q.options.map((opt, i) => (
        <option key={i} value={i}>
          Option {i + 1}: {opt.text || "(empty)"}
        </option>
      ))}
    </select>
  )}

  {/* TRUE / FALSE */}
  {q.type === "truefalse" && (
    <select
      value={q.answer}
      onChange={(e) =>
        handleQuestionChange(qIndex, "answer", e.target.value)
      }
      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
    >
      <option value="">Select Answer</option>
      <option value="true">True</option>
      <option value="false">False</option>
    </select>
  )}

  {/* TEXT */}
  {q.type === "text" && (
    <input
      type="text"
      placeholder="Enter Correct Answer"
      value={q.answer}
      onChange={(e) =>
        handleQuestionChange(qIndex, "answer", e.target.value)
      }
      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
    />
  )}

</div>
        </div>
      ))}

      {/* Add Question */}
      <div className="text-center mb-6">
        <button
          onClick={handleAddQuestion}
          className="bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 transition flex items-center justify-center mx-auto gap-2 shadow-md"
        >
          <PlusCircle size={20} /> Add Question
        </button>
      </div>

      {/* Save + Share Buttons */}
      <div className="flex justify-center gap-5 mt-8">
        <button
          onClick={handleSubmit}
          className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition flex items-center gap-2"
        >
          <Save size={20} /> Save Test
        </button>

        <button
          onClick={handleShare}
          className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition flex items-center gap-2"
        >
          <Share2 size={20} /> Share
        </button>
      </div>

      {/* Share Link */}
      {createdTestId && (
        <div className="mt-8 text-center bg-gray-50 border border-gray-200 rounded-xl p-5 shadow-sm">
          <p className="font-semibold text-gray-700 mb-2">Share this link:</p>
          <p className="text-indigo-600 font-medium break-all">
            {window.location.origin}/test/{createdTestId}
          </p>
        </div>
      )}
    </div>
  );
}

export default CreatePublicTest;
